/**
 * Site is a serializable class that contains stats for a single url for a logging session.
 */
import { Site } from './Site.mjs'

export class GroupSite {
  type = 'GroupSite'
  constructor({ sites = {}, url, title, faviconUrl }) {
    this.url = url
    if (faviconUrl) this._faviconUrl = faviconUrl
    if (title) this._title = title

    this.sites = Object.keys(sites).reduce((acc, key) => {
      acc[key] = new Site(sites[key])
      return acc
    }, {})
  }

  combine(otherSite) {
    const existingSite = this.sites[otherSite.url]
    if (existingSite) {
      existingSite.combine(otherSite)
    } else {
      this.sites[otherSite.url] = new Site(otherSite.toJson())
    }
  }

  /** @deprecated **/
  get startTime() {
    const urls = Object.keys(this.sites)
    if (urls.length === 0) return null
    const firstFocus = Math.min(
      ...urls.map((url) => this.sites[url].firstFocus || Infinity),
    )
    return new Date(firstFocus || 0).toISOString()
  }

  /** @deprecated **/
  get endTime() {
    const urls = Object.keys(this.sites)
    if (urls.length === 0) return null
    const lastFocus = Math.max(
      ...urls.map((url) => this.sites[url].lastFocus || 0),
    )
    return lastFocus
  }

  get firstFocus() {
    const urls = Object.keys(this.sites)
    if (urls.length === 0) return null
    return Math.min(
      ...urls.map((url) => this.sites[url].firstFocus || Infinity),
    )
  }

  get lastFocus() {
    const urls = Object.keys(this.sites)
    if (urls.length === 0) return null
    return Math.max(...urls.map((url) => this.sites[url].lastFocus || 0))
  }

  get time() {
    const urls = Object.keys(this.sites)
    if (urls.length === 0) return 0
    return urls.reduce((acc, url) => acc + (this.sites[url].time || 0), 0)
  }

  get faviconUrl() {
    if (this._faviconUrl) return this._faviconUrl
    const urls = Object.keys(this.sites)
    if (urls.length === 0) return ''
    // return the favicon of the site with the most time spent
    let maxTime = -1
    let faviconUrl = ''
    urls.forEach((url) => {
      const site = this.sites[url]
      if (site.time > maxTime) {
        maxTime = site.time
        faviconUrl = site.faviconUrl
      }
    })
    return faviconUrl
  }
  get title() {
    if (this._title) return this._title
    if (this.sites[this.url]?.title) return this.sites[this.url].title
    const urls = Object.keys(this.sites)
    if (urls.length === 0) return ''
    // return the title of the site with the most time spent
    let maxTime = -1
    let title = ''
    urls.forEach((url) => {
      const site = this.sites[url]
      if (site.time > maxTime) {
        maxTime = site.time
        title = site.title
      }
    })
    return title
  }

  get otherTitles() {
    const urls = Object.keys(this.sites)
    if (urls.length === 0) return []
    const titles = new Set()
    urls.forEach((url) => {
      const site = this.sites[url]
      site.otherTitles.forEach((title) => titles.add(title))
      if (site.title !== this.title) {
        titles.add(site.title)
      }
    })
    return Array.from(titles)
  }

  set endTime(endTime) {
    throw new Error(
      'Cannot set endTime on GroupSite, set it on individual sites instead',
    )
  }

  set startTime(startTime) {
    throw new Error(
      'Cannot set startTime on GroupSite, set it on individual sites instead',
    )
  }

  set firstFocus(firstFocus) {
    throw new Error(
      'Cannot set firstFocus on GroupSite, set it on individual sites instead',
    )
  }

  set lastFocus(lastFocus) {
    throw new Error(
      'Cannot set lastFocus on GroupSite, set it on individual sites instead',
    )
  }

  set time(time) {
    throw new Error(
      'Cannot set time on GroupSite, set it on individual sites instead',
    )
  }

  set title(title) {
    this._title = title
  }

  set otherTitles(otherTitles) {
    throw new Error(
      'Cannot set otherTitles on GroupSite, set it on individual sites instead',
    )
  }

  /**
   * Convert this state to a plain JSON object.
   * @returns {{assignmentId: string, courseId: string, organizationId: string, id: string, startTime: number, endTime: number|null, userId: string}}
   */
  toJson() {
    return {
      url: this.url,
      title: this._title,
      sites: Object.keys(this.sites).reduce((acc, key) => {
        acc[key] = this.sites[key].toJson()
        return acc
      }, {}),
    }
  }

  equals(other) {
    return (
      this.url === other.url &&
      this.firstFocus === other.firstFocus &&
      this.lastFocus === other.lastFocus &&
      this.time === other.time &&
      this.title === other.title &&
      this.otherTitles.length === other.otherTitles.length &&
      this.otherTitles.every((title) => other.otherTitles.includes(title))
    )
  }
}
